import axios from 'axios';

const api = axios.create({ baseURL: '/api' });

// ── Read-only static mode ───────────────────────────────────────────────────
// When built with REACT_APP_STATIC=1 the app serves a frozen snapshot of one
// review (bundled JSON + pre-generated PDFs) with no backend — used for the
// permanent, laptop-off demo link. The normal local app (flag unset) is untouched.
const STATIC = process.env.REACT_APP_STATIC === '1';

let S = {};
if (STATIC) {
  S = {
    reviews: require('../staticData/reviews.json'),
    results: require('../staticData/results.json'),
    review: require('../staticData/review.json'),
    documents: require('../staticData/documents.json'),
    documentTypes: require('../staticData/documentTypes.json'),
    propertyTypes: require('../staticData/propertyTypes.json'),
  };
}
const ok = (data) => Promise.resolve(data);

export const getDocumentTypes = () =>
  STATIC ? ok(S.documentTypes) : api.get('/document-types').then(r => r.data);
export const getPropertyTypes = () =>
  STATIC ? ok(S.propertyTypes) : api.get('/property-types').then(r => r.data);

export const listReviews = () =>
  STATIC ? ok((S.reviews || []).filter(r => r.id === 16)) : api.get('/reviews').then(r => r.data);
export const createReview = (data) =>
  STATIC ? Promise.reject(new Error('read-only preview')) : api.post('/reviews', data).then(r => r.data);
export const getReview = (id) =>
  STATIC ? ok(S.review) : api.get(`/reviews/${id}`).then(r => r.data);
export const deleteReview = (id) =>
  STATIC ? ok({}) : api.delete(`/reviews/${id}`);

export const listDocuments = (reviewId) =>
  STATIC ? ok(S.documents) : api.get(`/reviews/${reviewId}/documents`).then(r => r.data);
export const uploadDocuments = (reviewId, formData) =>
  STATIC ? Promise.reject(new Error('read-only preview'))
         : api.post(`/reviews/${reviewId}/documents`, formData, {
             headers: { 'Content-Type': 'multipart/form-data' }
           }).then(r => r.data);
export const updateDocument = (reviewId, docId, data) =>
  STATIC ? ok(data) : api.patch(`/reviews/${reviewId}/documents/${docId}`, data).then(r => r.data);
export const deleteDocument = (reviewId, docId) =>
  STATIC ? ok({}) : api.delete(`/reviews/${reviewId}/documents/${docId}`);

export const startAnalysis = (reviewId) =>
  STATIC ? Promise.reject(new Error('read-only preview')) : api.post(`/reviews/${reviewId}/analyze`).then(r => r.data);
export const getRunStatus = (runId) =>
  STATIC ? ok(S.results.run || {}) : api.get(`/runs/${runId}/status`).then(r => r.data);
export const resumeAnalysis = (runId) =>
  STATIC ? Promise.reject(new Error('read-only preview')) : api.post(`/runs/${runId}/resume`).then(r => r.data);
export const getLatestRun = (reviewId) =>
  STATIC ? ok(S.results.run || {}) : api.get(`/reviews/${reviewId}/latest-run`).then(r => r.data);

export const getResults = (reviewId) =>
  STATIC ? ok(S.results) : api.get(`/reviews/${reviewId}/results`).then(r => r.data);
export const getFindings = (reviewId, params) =>
  STATIC ? ok(S.results.findings || []) : api.get(`/reviews/${reviewId}/findings`, { params }).then(r => r.data);
// In static mode an override can't persist — resolve so the UI updates locally without erroring.
export const updateFinding = (findingId, data) =>
  STATIC ? ok(data) : api.patch(`/findings/${findingId}`, data).then(r => r.data);

// In static mode the demo ships pre-generated PDFs per (type, mode, language):
//   /demo/report_{standard|custom}_{en|nl}.pdf  and  /demo/summary_{en|nl}.pdf
// Only EN + NL are baked; any other language falls back to EN so the download is
// always a valid PDF. The chosen variant is encoded in the returned filename and
// resolved back to a URL in downloadReport().
const DEMO_LANGS = ['en', 'nl'];
const demoLang = (lang) => (DEMO_LANGS.includes(lang) ? lang : 'en');
export const exportReport = (reviewId, mode = 'standard', lang = 'en') =>
  STATIC ? ok({ filename: `DossierProof_Report_${mode === 'custom' ? 'custom' : 'standard'}_${demoLang(lang)}.pdf` })
         : api.post(`/reviews/${reviewId}/export`, null, { params: { mode, lang } }).then(r => r.data);
export const exportClientSummary = (reviewId, lang = 'en', mode = 'standard') =>
  STATIC ? ok({ filename: `DossierProof_ClientSummary_${demoLang(lang)}.pdf` })
         : api.post(`/reviews/${reviewId}/client-summary`, null, { params: { lang, mode } }).then(r => r.data);
export const downloadReport = (reviewId, filename) => {
  if (STATIC) {
    const fn = (filename || '').toLowerCase();
    const m = fn.match(/_([a-z]{2})\.pdf$/);
    const lang = m ? demoLang(m[1]) : 'en';
    const url = /summary/.test(fn)
      ? `/demo/summary_${lang}.pdf`
      : `/demo/report_${fn.includes('custom') ? 'custom' : 'standard'}_${lang}.pdf`;
    return fetch(url).then(r => r.blob());
  }
  return api.get(`/reviews/${reviewId}/download/${filename}`, { responseType: 'blob' }).then(r => r.data);
};
